import { each, flow, filter } from 'lodash';
import { curriedMap, curriedFilter, curriedSortBy } from '@adjust/dashboard-core/lib/fp';
import MouseSensor from './sensor/mouse-sensor';
import AnimationFrameScheduler from './util/animation-frame-scheduler';

function dndDraggable() {
  'ngInject';
  return {
    restrict: 'A',
    scope: {},
    controller: 'DraggableController',
    controllerAs: 'vm',
    bindToController: {
      disabled: '=',
      target: '@',
      sortable: '@',
      onDragFinish: '&',
      onSort: '&'
    }
  };
}

function DraggableController($element, $scope, Sortable, draggableClasses) {
  'ngInject';

  const vm = this;
  const animationFrame = AnimationFrameScheduler(handleSortMove);
  const sensor = new MouseSensor($element, {
    handleClass: draggableClasses.handle,
    itemClass: draggableClasses.item,
    handleSize: 0
  });

  let _placeholder = null;
  let _background = null;
  let _targetSortable = null;
  let _targetDroppable = null;
  let _isNewLocation = false;
  let _isActive = false;
  let _isMoving = false;
  let _isDragging = false;

  $element
    .on('draggable:start', handleSortStart)
    .on('draggable:move', animationFrame)
    .on('draggable:finish', handleSortFinish)
    .addClass(draggableClasses.draggable);

  if (vm.sortable) {
    $element.addClass(draggableClasses.sortable);
  }

  $scope.$on('$destroy', destroy);

  /**
   * Handles sort start
   * @param {CustomEvent} event
   * @private
   */
  function handleSortStart(event) {

    if (vm.disabled || _isDragging) {
      event.preventDefault();
      return;
    }

    if (vm.sortable) {
      Sortable.initialSort($element.children());
    }

    let {target, position, margin} = event.detail;
    let inlineStyle = getInlineStyle(position, margin);

    _placeholder = createPlaceholderElement();
    _background = createBackgroundElement();

    Object.assign(_placeholder.style, inlineStyle);
    Object.assign(target.style, inlineStyle);

    target.classList.add(draggableClasses.itemActive);
    target.after(_placeholder);

    document.body.after(_background);
    document.body.classList.add(draggableClasses.body);

    if (!vm.sortable) {
      Sortable.removeIndex(target);
    }

    $element
      .addClass(draggableClasses.active)
      .removeClass(draggableClasses.drop);

    _isDragging = true;
  }

  /**
   * Handles sort move
   * @param {CustomEvent} event
   * @private
   */
  function handleSortMove({detail}) {

    let {target, originalTarget} = detail;
    let {droppable, sortable, isNewLocation} = getValidTargets(target);

    originalTarget.style.transform = `translate3d(${detail.x}px, ${detail.y}px, 0)`;

    if (droppable) {
      _targetDroppable = droppable;
      _isNewLocation = isNewLocation;
      handleSortActive(originalTarget);
    }

    if (sortable) {

      // Sorting into a different list
      if (_isNewLocation && isNaN(Sortable.getIndex(originalTarget))) {
        Sortable.initialSort(droppable.children);
        Sortable.setIndex(originalTarget, droppable.children.length);
      }

      handleSortEnter(sortable, originalTarget, droppable.children);
    }
  }

  /**
   *
   * @param {HTMLElement} target
   * @param {HTMLElement} originalTarget
   * @param {HTMLElement} children
   * @private
   */
  function handleSortEnter(target, originalTarget, children) {

    if (_isMoving) {
      return;
    }

    let currentIndex = Sortable.getIndex(originalTarget);
    let targetIndex = Sortable.getIndex(target);
    let elements = Sortable.getElementsInRange(children, currentIndex, targetIndex);
    let isMovingTargetDown = currentIndex > targetIndex;
    let animations = [];

    _isMoving = true;
    _targetSortable = target;

    each(elements, function(item) {

      item.savedTranslationY = getItemTranslationY(item, originalTarget, isMovingTargetDown);
      animations.push(animate(item, item.savedTranslationY));

    });

    Sortable.update(elements, currentIndex, targetIndex);
    Sortable.setIndex(originalTarget, targetIndex);

    Promise.all(animations).then(function() {
      _isMoving = false;
    });

  }

  /**
   * @param {HTMLElement} target
   * @private
   */
  function handleSortCancel(target) {

    if (target.style.transform && target.style.transform !== 'none') {

      $element.addClass(draggableClasses.drop);

      animate(target, 0).then(function() {
        clear(target);
        $element.removeClass(draggableClasses.active, draggableClasses.drop);
      });

      return;
    }

    clear(target);

  }

  /**
   *
   * @param originalTarget
   */
  function handleSortActive(originalTarget) {

    if (_isActive) {
      return;
    }

    _isActive = true;
    _targetDroppable.classList.remove(draggableClasses.drop);

    if (_isNewLocation) {
      _targetDroppable.classList.add(draggableClasses.moving);
    }

    angular
      .element(_targetDroppable)
      .one('mouseleave', function() {
        _targetDroppable.classList.remove(draggableClasses.moving);

        if (!vm.sortable) {
          Sortable.removeIndex(originalTarget);
        }

        reset(_targetDroppable);
      });
  }

  /**
   * @param {CustomEvent} event
   * @private
   */
  function handleSortFinish({detail}) {

    animationFrame.cancel();

    if (!_targetDroppable) {
      handleSortCancel(detail.originalTarget);
      return;
    }

    $element.addClass(draggableClasses.drop);
    _targetDroppable.classList.add(draggableClasses.drop);

    if (_targetSortable && !_isNewLocation) {
      handleOnSort(_targetDroppable.children);
    }

    if (_targetDroppable && _isNewLocation) {
      handleOnDragFinish(detail.originalTarget);
    }

    clear(detail.originalTarget);
    reset(_targetDroppable);
  }

  /**
   * @param children
   */
  function handleOnSort(children) {

    let order = flow(
      curriedFilter(child => !child.classList.contains(draggableClasses.placeholder)),
      curriedSortBy(child => Sortable.getIndex(child)),
      curriedMap(child => child.dataset.draggable)
    )(children);

    vm.onSort({
      $event: {order}
    });

    $scope.$apply();
  }

  /**
   * Callback for drop operation
   */
  function handleOnDragFinish(target) {

    let token = target.dataset.draggable;
    let targetToken = _targetSortable ? _targetSortable.dataset.draggable : null;
    let after = false;

    if (targetToken) {
      let currentIndex = Sortable.getIndex(target);
      let targetIndex = Sortable.getIndex(_targetSortable);

      after = currentIndex > targetIndex;
    }

    vm.onDragFinish({
      $event: {
        token,
        targetToken,
        after
      }
    });

    $scope.$apply();
  }

  /**
   * @param {HTMLElement} target
   * @returns {Object}
   */
  function getValidTargets(target) {

    // Sorting
    let closestValidSortable = target.closest(`.${draggableClasses.item}`);
    let parentElement = closestValidSortable ? closestValidSortable.parentElement : null;
    let isSortable = parentElement ? Sortable.isSortable(parentElement) : false;

    if (isSortable) {
      return {
        sortable: closestValidSortable,
        droppable: parentElement,
        isNewLocation: parentElement && parentElement !== $element[0]
      };
    }

    let closestValidTarget = vm.target && target.closest(`${vm.target}`);

    return {
      sortable: null,
      droppable: closestValidTarget,
      isNewLocation: closestValidTarget && closestValidTarget !== $element[0]
    };

  }

  /**
   * @param {HTMLElement} target
   * @param {Number} y
   * @param {Number} x
   * @private
   */
  function animate(target, y, x = 0) {

    return new Promise(function(resolve) {

      if (target.animating) {
        return;
      }

      target.animating = true;
      target.style.transform = `translate3d(${x}px, ${y}px, 0)`;

      angular
        .element(target)
        .one('transitionend', function() {
          target.animating = false;
          resolve();
        });
    });

  }

  /**
   * @param {HTMLElement} item
   * @param {HTMLElement} originalTarget
   * @param {Boolean} isMovingTargetDown
   * @private
   */
  function getItemTranslationY(item, originalTarget, isMovingTargetDown) {

    const savedTranslationY = item.savedTranslationY || 0;
    const height = originalTarget.offsetHeight + ((originalTarget.offsetHeight - originalTarget.clientHeight) * 2);

    return savedTranslationY + (isMovingTargetDown ? height : -height);
  }

  /**
   * @returns {HTMLElement}
   * @private
   */
  function createPlaceholderElement() {

    const placeholder = document.createElement('li');
    placeholder.classList.add(draggableClasses.placeholder);

    return placeholder;
  }

  /**
   * @private
   */
  function createBackgroundElement() {

    const background = document.createElement('div');
    background.classList.add(draggableClasses.background);

    return background;
  }

  /**
   * @param {HTMLElement} droppable
   */
  function reset(droppable) {

    let children = filter(
      droppable.children,
      child => !child.classList.contains(draggableClasses.placeholder)
    );

    _targetSortable = null;
    _targetDroppable = null;
    _isNewLocation = false;
    _isActive = false;
    _isMoving = false;

    each(children, function(child) {
      child.style.removeProperty('transform');
      child.savedTranslationY = 0;
      child.animating = false;
    });

    if (Sortable.isSortable(droppable)) {
      Sortable.initialSort(children);
    }

  }

  /**
   * @param {HTMLElement} target
   * @private
   */
  function clear(target) {

    _isDragging = false;

    _placeholder.remove();
    _background.remove();

    document.body.classList.remove(draggableClasses.body);
    target.classList.remove(draggableClasses.itemActive);

    Object.assign(target.style, {
      width: 'auto',
      height: 'auto',
      top: 'auto',
      right: 'auto',
      bottom: 'auto',
      left: 'auto',
      transform: 'none'
    });

    if (_targetDroppable) {
      angular.element(_targetDroppable).off('mouseleave');
      _targetDroppable.classList.remove(draggableClasses.active, draggableClasses.moving);
      $element.removeClass(draggableClasses.active);
    }
  }

  /**
   * @param position {ClientRect | DOMRect}
   * @param {Object} margin
   * @returns {Object}
   * @private
   */
  function getInlineStyle(position, margin) {
    return {
      width: `${position.width}px`,
      height: `${position.height}px`,
      top: `${position.top}px`,
      right: `${position.right}px`,
      bottom: `${position.bottom}px`,
      left: `${position.left}px`,
      marginTop: `${margin.top}px`,
      marginRight: `${margin.right}px`,
      marginBottom: `${margin.bottom}px`,
      marginLeft: `${margin.left}px`
    };
  }

  /**
   * @private
   */
  function destroy() {

    sensor.destroy();

    $element
      .off('draggable:start', handleSortStart)
      .off('draggable:move', handleSortMove)
      .off('draggable:finish', handleSortFinish);
  }
}

export {dndDraggable, DraggableController};
